import { api } from 'api';
import { DEBOUNCE_SEC } from 'constants';
import {
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
  PAYID_TYPE_MOBILE,
  PAYTO_VALIDATE_BANK_VALIDATION_FAILURE_RESULT_CODES,
  PAYTO_VALIDATE_BANK_VALIDATION_RESULT_CODE,
  PAYTO_VALIDATE_BANK_VALIDATION_SUCCESS_RESULT_CODES,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import {
  checkMaintenance,
  resolveSystemErrorContent,
  resolveSystemErrorTitle,
} from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useDebounceCallback from 'hooks/useDebounceCallback';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { stateStore } from 'store';
import { SET_SHOW_LOADING } from 'store/action';

const withCheckMobileNumber = (Component) => {
  const CheckMobileNumberComponent = (props) => {
    const { information, setValidation, onPaytoSystemError } = props || {};

    const {
      dispatch,
      payIdType,
      setPaytoPayIdGeneration,
      setPaytoCreateLimit,
      setPaytoAmendLimit,
      setPaytoPayIdCheckPending,
      setPaytoAccountInformation,
      setPaytoPayIdBankValidationFailure,
    } = usePaytoStore();
    const { token } = useAuth();

    // Ref-counted guard so this HOC only clears the shared loading flag once
    // none of ITS OWN in-flight validations remain (defect d).
    const pendingRequestsRef = useRef(0);

    // DEV-1401: distinguishes this instance's very first effect run (mount,
    // e.g. modal opened with a prefilled PayID) from a later run caused by
    // the user's own typing, so the mount-only safety-net clear below can't
    // fire on every keystroke.
    const hasRunEffectRef = useRef(false);

    // Generation guard (payto/phone validator race fix): bumped in the
    // SHARED store (usePaytoStore's paytoPayIdGeneration, backed by
    // stateStore) whenever a new value enters the debounce cycle for the
    // shared PayId field, OR whenever the PayId type itself is switched
    // (bumped by FormInputEmailMobileValidation.jsx's
    // handlePayIdTypeOnChange). A validatePayId response is only applied to
    // state if the generation it was requested under still matches
    // `stateStore.paytoPayIdGeneration` at resolution time, so a stale
    // response can never clobber a fresher one.
    //
    // This is intentionally store-backed rather than a per-instance
    // useRef: a type switch unmounts this instance and mounts the OTHER
    // type's instance in the SAME commit (React does not give the
    // outgoing instance an extra render/effect pass first), so a
    // per-instance ref (or a ref mirrored via a useEffect keyed on the
    // hook's `paytoPayIdGeneration` value) would never observe the bump --
    // it simply stops existing before that effect could fire. `stateStore`
    // is a module-level variable that `store/index.js` updates
    // synchronously during `StateProvider`'s render (before children are
    // reconciled), so it is guaranteed to already reflect the bump by the
    // time this (about-to-be-unmounted) instance's in-flight request
    // resolves. This is the same pattern already used elsewhere in this
    // codebase (see helpers/maintenance.js) to read live global state from
    // outside a component's own render lifecycle.

    // Latest value entering the pipeline, kept in a ref (not state) purely
    // so the stable shouldApplyPhoneValidation predicate below can read it
    // live at write-time, regardless of which render's closure ends up
    // invoking it.
    const latestPayIdValueRef = useRef(
      information?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]
    );

    const onCheckPayIdValidation = async (
      payId,
      requestedPayIdType,
      name,
      generation
    ) => {
      pendingRequestsRef.current += 1;
      dispatch({ type: SET_SHOW_LOADING, payload: true });
      // DEV-1401: per-call pending signal, deliberately separate from the
      // shared SET_SHOW_LOADING dispatch above (see row #5).
      setPaytoPayIdCheckPending(true);

      try {
        const { data } = await api.validatePayId(
          token,
          payId,
          requestedPayIdType
        );

        const { resultCode, supportsPayTo, createPayToLimit, amendPayToLimit } =
          data || {};

        const isSuccess =
          PAYTO_VALIDATE_BANK_VALIDATION_SUCCESS_RESULT_CODES.includes(
            resultCode
          );
        const isKnownFailure =
          PAYTO_VALIDATE_BANK_VALIDATION_FAILURE_RESULT_CODES.includes(
            resultCode
          );
        const isUnrecognised = !isSuccess && !isKnownFailure;

        if (isUnrecognised) {
          console.error(`PayTo validatePayId: unrecognised resultCode`, {
            resultCode,
            requestedPayIdType,
          });
        }

        // DEV-1401: supportsPayTo === false takes priority over the
        // resultCode-derived message -- a PayID can resolve successfully
        // (MTCH/VFYD) and still not support PayTo, and that case must not
        // be silently treated as valid just because resultCode looked fine.
        const isNotSupported = supportsPayTo === false;

        // Success (MTCH/VFYD) clears the field error. A known failure code
        // (CMTC/NMTC/NFND/CLSD) sets its mapped message. Unrecognised codes
        // are logged for investigation and treated as failure using the
        // existing generic message; a distinct user-facing message for this
        // case would need a new translation key, not added here.
        const message = isNotSupported
          ? FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES?.[
              requestedPayIdType
            ]?.msgNotSupported
          : isSuccess
            ? ''
            : isKnownFailure
              ? PAYTO_VALIDATE_BANK_VALIDATION_RESULT_CODE?.[resultCode]
              : FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES?.[
                  requestedPayIdType
                ]?.msgInvalid;

        // Only apply if a newer value (or a type switch) hasn't superseded
        // this request. Read the live store value directly rather than a
        // per-instance ref/state snapshot -- see the generation-guard
        // comment above for why this must survive this instance unmounting.
        // Applies uniformly to both Mobile and Email, since generation is
        // bumped and threaded through for both paths.
        if (stateStore.paytoPayIdGeneration === generation) {
          setValidation((prev) => ({
            ...prev,
            [name]: message,
          }));

          // DEV-1401: drives the Create PayTo modal's separate PayID
          // failure row -- true only for a known bank-validation failure
          // (NFND/CLSD) or an unrecognised resultCode, false for success
          // and for supportsPayTo===false (isNotSupported implies isSuccess
          // here, so both are already false in that case).
          setPaytoPayIdBankValidationFailure(isKnownFailure || isUnrecognised);

          // DEV-1401: only adopt the limit when the PayID actually
          // resolved successfully and supports PayTo, and only when this
          // response actually repeated the field -- mirrors the same
          // hasOwnProperty guard used on the BSB side
          // (withCheckBsbAccount.jsx), so a response that omits
          // createPayToLimit preserves whatever limit is already stored
          // instead of nulling it out.
          if (
            isSuccess &&
            !isNotSupported &&
            Object.prototype.hasOwnProperty.call(data || {}, 'createPayToLimit')
          ) {
            setPaytoCreateLimit(createPayToLimit);

            // DEV-1401: BSB-parity existence gate for the maxAmount
            // dropdown (mirrors bankCode, withCheckBSBNumber.jsx) -- records
            // the exact PayID value/type this successful, non-stale
            // (generation-gated) response confirmed, so popup/index.jsx can
            // require this to still match the CURRENT value/type before
            // showing maxAmount, instead of relying only on the pending
            // flag (which for MOBILE never turns true during the earlier
            // phone-format-check phase). Read the live store value rather
            // than a possibly-stale closed-over paytoAccountInformation,
            // consistent with the generation check just above.
            setPaytoAccountInformation({
              ...(stateStore.paytoAccountInformation || {}),
              confirmedPayId: payId,
              confirmedPayIdType: requestedPayIdType,
            });
          }

          // DEV-1401: mirrors the createPayToLimit guard immediately above,
          // as a separate independent hasOwnProperty check on amendPayToLimit
          // -- see withCheckBsbAccount.jsx's guard comment for why the two
          // fields are gated independently rather than jointly.
          if (
            isSuccess &&
            !isNotSupported &&
            Object.prototype.hasOwnProperty.call(data || {}, 'amendPayToLimit')
          ) {
            setPaytoAmendLimit(amendPayToLimit);
          }
        }
      } catch (error) {
        checkMaintenance(error);
        console.error(error?.message);

        // Only apply if a newer value (or a type switch) hasn't superseded
        // this request. Applies uniformly to both Mobile and Email, since
        // generation is bumped and threaded through for both paths.
        if (stateStore.paytoPayIdGeneration === generation) {
          // DEV-1401: additive -- see withCheckBSBNumber.jsx for the same
          // pattern, rationale, and 5xx-only httpErrors gate. Placed inside
          // this same generation guard (this file has no early-return
          // shape like the other two HOCs) so a stale/superseded failure
          // never surfaces the overlay. Existing inline poisoning below is
          // unchanged.
          const failureStatus = error?.response?.status;
          const isServerSystemFailure =
            typeof failureStatus === 'number' && failureStatus >= 500;
          const systemErrorContent = resolveSystemErrorContent(error, {
            httpErrors: isServerSystemFailure,
          });
          if (systemErrorContent != null) {
            onPaytoSystemError?.(
              resolveSystemErrorTitle(error, {
                httpErrors: isServerSystemFailure,
              }),
              systemErrorContent,
            );
          }

          setValidation((prev) => ({
            ...prev,
            [name]:
              FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES?.[
                requestedPayIdType
              ]?.msgInvalid,
          }));

          // DEV-1401: a transport/system-error failure is not a bank
          // validation failure -- never show the separate PayID failure
          // row for this path (the system-error overlay handles it).
          setPaytoPayIdBankValidationFailure(false);
        }
      } finally {
        pendingRequestsRef.current -= 1;
        // DEV-1401: generation-gated, mirroring the message-write guards
        // above -- a stale/superseded generation's own completion must not
        // clear loading that now belongs to a newer active sequence
        // (e.g. after a MOBILE<->EMAIL PayID type switch).
        if (
          pendingRequestsRef.current <= 0 &&
          stateStore.paytoPayIdGeneration === generation
        ) {
          dispatch({ type: SET_SHOW_LOADING, payload: false });
          setPaytoPayIdCheckPending(false);
        }
      }
    };

    const { debounce: handleCheckPayId } = useDebounceCallback(
      (payId, requestedPayIdType, name, generation) => {
        // DEV-1442: the useDebounceCallback hook has no cancel, so a timer
        // armed for an earlier valid value still fires after the input
        // became invalid/empty. Drop it here, before onCheckPayIdValidation
        // sets any loading/pending flag, so no flag is left stranded by the
        // generation-gated finally block.
        if (payId !== latestPayIdValueRef.current) {
          return;
        }
        onCheckPayIdValidation(payId, requestedPayIdType, name, generation);
      },
      DEBOUNCE_SEC
    );

    // Sequencing (payto/phone validator race fix): fires only after the
    // mobile-number component's OWN phone-format validation has already
    // succeeded for this exact value (see shouldApplyPhoneValidation /
    // onPhoneFormatValidated below, threaded into Component as additive,
    // optional, no-op-by-default props). This is what makes validatePayId
    // run strictly after validatePhone instead of racing it, without this
    // HOC having to duplicate the inner component's format/prefix gating
    // logic (it doesn't have access to `config`'s min/max length or prefix
    // rules, and re-deriving them here would be meaningful duplication).
    const handlePhoneFormatValidated = useCallback(
      (payId) => {
        if (payId !== latestPayIdValueRef.current) {
          // A newer value has already superseded this one; do not chase it.
          return;
        }

        handleCheckPayId(
          payId,
          PAYID_TYPE_MOBILE,
          FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
          stateStore.paytoPayIdGeneration
        );
      },
      [handleCheckPayId]
    );

    // Stable predicate (identity never changes across renders) so the
    // mobile component's own debounced write of its phone-format result
    // cannot land after a newer value has already moved on. Reads
    // latestPayIdValueRef live at call time rather than closing over a
    // render-time snapshot, since the debounced call that invokes it may
    // run long after the render that created it.
    const shouldApplyPhoneValidation = useCallback(
      (value) => value === latestPayIdValueRef.current,
      []
    );

    // DEV-1442: MOBILE only (passed as onPhoneInputChange). The mobile input
    // only commits `information` for a check-ready number, so for an
    // incomplete/invalid edit the effect below never re-runs and the ref
    // keeps the last valid number -- letting a pending check for that number
    // still pass shouldApplyPhoneValidation. Track the live value here, and
    // when it is not check-ready supersede any pending/in-flight check and
    // drop the previously confirmed PayID so a stale one cannot be submitted.
    const handlePhoneInputChange = (value, isCheckReady) => {
      latestPayIdValueRef.current = value;
      if (isCheckReady) {
        return;
      }
      setPaytoPayIdGeneration((stateStore.paytoPayIdGeneration || 0) + 1);
      // Requests still in flight belong to the superseded generation, and the
      // generation-gated finally block in onCheckPayIdValidation will not
      // clear loading for them. Reset the count so they cannot hold a newer
      // request's loading open once that one completes (the finally gate is
      // `<= 0`, so a stale completion driving the count negative is safe).
      pendingRequestsRef.current = 0;
      dispatch({ type: SET_SHOW_LOADING, payload: false });
      setPaytoPayIdCheckPending(false);
      setPaytoPayIdBankValidationFailure(false);
      if (stateStore.paytoAccountInformation?.confirmedPayId) {
        setPaytoAccountInformation({
          ...stateStore.paytoAccountInformation,
          confirmedPayId: '',
          confirmedPayIdType: '',
        });
      }
    };

    useEffect(() => {
      const payId = information?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME];

      latestPayIdValueRef.current = payId;

      // Compute the bumped value ourselves rather than reading
      // `stateStore.paytoPayIdGeneration` back immediately after
      // dispatching: the store update is not synchronous, so reading it
      // back here could still observe the pre-bump value. Using the
      // locally-computed `newGeneration` for both the dispatch and the
      // armed request value sidesteps that timing dependency entirely.
      const newGeneration = (stateStore.paytoPayIdGeneration || 0) + 1;
      setPaytoPayIdGeneration(newGeneration);

      // DEV-1401: safety-net terminal clear. Bumping the generation above
      // can make the finally-block gate in onCheckPayIdValidation suppress
      // a stale generation's own SET_SHOW_LOADING:false (see that guard's
      // comment), so an emptied field always gets an explicit clear here
      // rather than relying on a now-suppressed stale completion. Safe to
      // run on every change (not just mount): an empty field should never
      // show this sequence as loading.
      if (isEmpty(payId)) {
        dispatch({ type: SET_SHOW_LOADING, payload: false });
        setPaytoPayIdCheckPending(false);
        setPaytoPayIdBankValidationFailure(false);
        hasRunEffectRef.current = true;
        return;
      }

      if (payIdType === PAYID_TYPE_MOBILE) {
        // Do not call validatePayId here for the mobile path -- the mobile
        // field's own phone-format validation runs independently inside
        // Component; validatePayId is only triggered once that succeeds,
        // via handlePhoneFormatValidated.
        //
        // DEV-1401: mount-only safety net. This effect also re-runs on
        // every keystroke (via the `information` dependency), which is
        // exactly when handleOnChange has just dispatched
        // SET_SHOW_LOADING:true for a freshly-armed check -- so this clear
        // must fire only on this instance's first run (mount/remount with
        // an already non-empty value, e.g. a retry with a prefilled PayID,
        // or a modal reopened on an existing account), never on a later
        // run caused by the user's own typing.
        if (!hasRunEffectRef.current) {
          dispatch({ type: SET_SHOW_LOADING, payload: false });
          setPaytoPayIdCheckPending(false);
          setPaytoPayIdBankValidationFailure(false);
        }
        hasRunEffectRef.current = true;
        return;
      }

      hasRunEffectRef.current = true;

      // DEV-1442: EMAIL only (MOBILE returned above). A value the field's own
      // format rule rejects must not trigger validatePayId, whose response
      // would overwrite the client-side format error. Uses the same rule the
      // input applies (emailValid) rather than the validation map, which also
      // carries server verdicts. Mirrors the empty-value branch's clears,
      // since the generation bump above can suppress a stale completion's
      // own clear.
      const checkValidFn =
        FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES?.[payIdType]
          ?.checkValidFn;
      if (checkValidFn && !checkValidFn(payId)) {
        dispatch({ type: SET_SHOW_LOADING, payload: false });
        setPaytoPayIdCheckPending(false);
        setPaytoPayIdBankValidationFailure(false);
        return;
      }

      handleCheckPayId(
        payId,
        payIdType,
        FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
        newGeneration
      );
    }, [information?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME], payIdType]);

    const newProps = {
      ...props,
      // Unconditional: this HOC only ever wraps the PayTo PayId mobile/email
      // input (both instances, per FormInputEmailMobileValidation.jsx), so
      // blur must never clear an existing message for either field --
      // unlike onPhoneFormatValidated/shouldApplyPhoneValidation below,
      // which are mobile-only concerns.
      suppressBlurValidation: true,
      ...(payIdType === PAYID_TYPE_MOBILE
        ? {
            onPhoneFormatValidated: handlePhoneFormatValidated,
            shouldApplyPhoneValidation,
            onPhoneInputChange: handlePhoneInputChange,
            // DEV-1401: tells FormInputMobileNumberValidation that a
            // successful phone-format check hands loading ownership off to
            // this HOC's own validatePayId call (onCheckPayIdValidation),
            // instead of clearing loading itself -- closes the flicker
            // where loading briefly went false between the two steps.
            hasSequencedLoadingHandoff: true,
          }
        : {}),
    };
    return <Component {...newProps} />;
  };
  return CheckMobileNumberComponent;
};

export default withCheckMobileNumber;
